import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';

export default function Privacy() {
  useEffect(() => { window.scrollTo(0, 0); }, []);
  const updated = 'February 2026';

  return (
    <div className="min-h-screen" style={{ background: '#FAFAFA', fontFamily: 'Manrope, sans-serif' }}>
      <header className="border-b border-gray-200 bg-white sticky top-0 z-40">
        <div className="max-w-5xl mx-auto px-6 h-14 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-1" data-testid="privacy-home-link">
            <span style={{ fontFamily: 'Bebas Neue', fontSize: '1.3rem', color: '#0F0F0F' }}>HUSCLE</span>
            <span style={{ fontFamily: 'Bebas Neue', fontSize: '1.3rem', color: '#FF2E00' }}>OS</span>
          </Link>
          <Link to="/" className="text-xs font-bold tracking-widest uppercase text-gray-500 hover:text-gray-900">Back to HuscleOS</Link>
        </div>
      </header>
      <div className="max-w-3xl mx-auto px-6 pt-16 pb-20">
        <p className="text-[#FF2E00] text-xs font-bold uppercase tracking-widest mb-3">Legal</p>
        <h1 className="text-4xl sm:text-5xl tracking-tight mb-2" style={{ fontFamily: 'Bebas Neue', color: '#0F0F0F' }}>PRIVACY POLICY</h1>
        <p className="text-gray-500 text-sm mb-10">Last updated: {updated}</p>

        <div className="space-y-8 text-gray-700 text-sm leading-relaxed">
          <section>
            <h2 className="font-bold text-lg mb-3 text-gray-900">1. Who we are</h2>
            <p>
              HuscleOS is gym software built for independent gym operators. This Privacy Policy describes how we collect, use, and protect personal information
              when you use huscleos.com or sign up for a HuscleOS subscription.
            </p>
          </section>

          <section>
            <h2 className="font-bold text-lg mb-3 text-gray-900">2. What we collect</h2>
            <ul className="list-disc pl-6 space-y-2">
              <li><strong>Account info</strong>: name, email, phone, gym name, billing address, payment metadata (processed by Stripe).</li>
              <li><strong>Customer data you upload</strong>: lead records, member data, campaigns, and content you create inside HuscleOS.</li>
              <li><strong>Usage data</strong>: pages visited, actions taken in the product, device + IP for security and analytics.</li>
              <li><strong>Cookies</strong>: authentication tokens, analytics (Google Analytics, PostHog), conversion tracking.</li>
            </ul>
          </section>

          <section>
            <h2 className="font-bold text-lg mb-3 text-gray-900">3. How we use it</h2>
            <ul className="list-disc pl-6 space-y-2">
              <li>To deliver the HuscleOS service you subscribed to.</li>
              <li>To send you transactional emails and SMS (account notifications, onboarding, drip campaigns you initiate).</li>
              <li>To improve our product through aggregated, anonymized analytics.</li>
              <li>To detect fraud, abuse, and policy violations.</li>
            </ul>
            <p className="mt-3">
              We do <strong>not</strong> sell your data to third parties, and we do not use your customer data (your leads, members, campaigns) to train AI models or cross-sell.
            </p>
          </section>

          <section>
            <h2 className="font-bold text-lg mb-3 text-gray-900">4. Third-party processors</h2>
            <p className="mb-3">We use the following vendors to operate HuscleOS. Each has its own privacy policy and data handling standards:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li><strong>Stripe</strong> — payment processing</li>
              <li><strong>Resend</strong> — transactional email delivery</li>
              <li><strong>Twilio</strong> — SMS delivery</li>
              <li><strong>MailerSend</strong> — SMS fallback</li>
              <li><strong>MongoDB Atlas</strong> — hosted database</li>
              <li><strong>Google Analytics &amp; PostHog</strong> — product analytics</li>
            </ul>
          </section>

          <section>
            <h2 className="font-bold text-lg mb-3 text-gray-900">5. Your rights</h2>
            <p className="mb-3">You can, at any time:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Request a copy of your data (GDPR/CCPA: right to access).</li>
              <li>Correct information we hold about you.</li>
              <li>Delete your account and data (subject to a 60-day retention window).</li>
              <li>Opt out of marketing emails via the unsubscribe link on every message.</li>
              <li>Residents of California (CCPA/CPRA) and the EU/UK (GDPR) have additional rights — contact us to exercise them.</li>
            </ul>
          </section>

          <section>
            <h2 className="font-bold text-lg mb-3 text-gray-900">6. Data retention</h2>
            <p>
              We keep account data while your subscription is active and for 60 days after cancellation (to allow reactivation and data export).
              Billing records are retained for 7 years to meet tax and accounting requirements.
            </p>
          </section>

          <section>
            <h2 className="font-bold text-lg mb-3 text-gray-900">7. Security</h2>
            <p>
              HuscleOS uses TLS for all traffic, bcrypt-hashed passwords, JWT-based authentication, and role-based access control.
              We follow industry best practices — but no system is 100% secure. If we detect a breach affecting your data, we will notify you within 72 hours.
            </p>
          </section>

          <section>
            <h2 className="font-bold text-lg mb-3 text-gray-900">8. SMS &amp; email compliance</h2>
            <p>
              When you send SMS or email campaigns from HuscleOS, you are the sender of record. You must obtain consent from your recipients and honor unsubscribe/STOP requests.
              HuscleOS automatically honors opt-outs at the platform level.
            </p>
          </section>

          <section>
            <h2 className="font-bold text-lg mb-3 text-gray-900">9. Children</h2>
            <p>HuscleOS is not intended for users under 16. We do not knowingly collect data from children.</p>
          </section>

          <section>
            <h2 className="font-bold text-lg mb-3 text-gray-900">10. Updates to this policy</h2>
            <p>
              We may update this Privacy Policy. Material changes will be emailed to account owners with at least 14 days' notice.
            </p>
          </section>

          <section>
            <h2 className="font-bold text-lg mb-3 text-gray-900">11. Contact</h2>
            <p>
              Questions? Email <a href="mailto:hustle@huscleos.com" className="text-[#FF2E00] font-semibold hover:underline">hustle@huscleos.com</a>.
            </p>
          </section>
        </div>
      </div>
      <footer className="border-t border-gray-200 bg-white py-8">
        <div className="max-w-5xl mx-auto px-6 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-gray-500">
          <p>© {new Date().getFullYear()} HuscleOS. All rights reserved.</p>
          <div className="flex items-center gap-6">
            <Link to="/privacy" className="hover:text-gray-900">Privacy</Link>
            <Link to="/terms" className="hover:text-gray-900">Terms</Link>
            <Link to="/" className="hover:text-gray-900">Home</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
